import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import Navbar from '../components/Navbar';
import { theme } from '../utils/theme';

const Profile = () => {
  const { t, i18n } = useTranslation('profile');
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [nameForm, setNameForm] = useState({ name: '' });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [preferencesForm, setPreferencesForm] = useState({
    defaultLanguage: '',
    newsletterConsent: false
  });
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let retries = 3;
    const fetchUser = async () => {
      try {
        const response = await api.get('/api/auth/me');
        setUser(response.data.user);
        setNameForm({ name: response.data.user.name });
        setPreferencesForm({
          defaultLanguage: response.data.user.defaultLanguage || 'en',
          newsletterConsent: response.data.user.newsletterConsent || false
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user:', err);
        if (retries > 0 && err.response?.status === 404) {
          retries--;
          // Wait 1 second before retrying
          setTimeout(fetchUser, 1000);
        } else {
          setLoading(false);
          navigate('/login');
        }
      }
    };
    fetchUser();
  }, [navigate]);

  const handleNameChange = (e) => {
    setNameForm({ name: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePreferencesChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setPreferencesForm(prev => ({
      ...prev,
      [e.target.name]: value
    }));
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.put('/api/auth/profile', {
        name: nameForm.name
      });

      // Update the stored token with the new one
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      setUser(response.data.user);
      // Update local storage user data
      localStorage.setItem('userData', JSON.stringify(response.data.user));
      window.dispatchEvent(new Event('authChange'));
      setSuccessMessage('Name updated successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update name');
    }
  };

  const handleUpdatePreferences = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.put('/api/auth/profile', {
        defaultLanguage: preferencesForm.defaultLanguage,
        newsletterConsent: preferencesForm.newsletterConsent
      });

      // Update the stored token with the new one
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      setUser(response.data.user);
      // Update local storage user data
      localStorage.setItem('userData', JSON.stringify(response.data.user));
      
      // Update i18n language if changed
      if (preferencesForm.defaultLanguage !== i18n.language) {
        i18n.changeLanguage(preferencesForm.defaultLanguage);
      }
      
      window.dispatchEvent(new Event('authChange'));
      setSuccessMessage('Preferences updated successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update preferences');
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      await api.put('/api/auth/profile', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      setSuccessMessage('Password updated successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete('/api/auth/account', {
        data: { password: deletePassword }
      });
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      window.dispatchEvent(new Event('authChange'));
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className={theme.page.default}>
      <Navbar />
      
      {/* Profile Header */}
      <div className="relative bg-gradient-to-r from-slate-700 to-slate-900 py-16 pt-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-slate-700 text-2xl font-bold shadow-lg">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{user?.name}</h1>
              <p className="text-white/80">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Alerts */}
          <div className="space-y-4 mb-8">
            {error && (
              <div className="p-4 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-r-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            {successMessage && (
              <div className="p-4 bg-green-50 border-l-4 border-green-400 text-green-700 rounded-r-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{successMessage}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Grid Layout for Forms */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-8">
              {/* Update Name Form */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-slate-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800">{t('profileInfo.title')}</h2>
                </div>
                <form onSubmit={handleUpdateName} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('profileInfo.nameLabel')}</label>
                    <input
                      type="text"
                      value={nameForm.name}
                      onChange={handleNameChange}
                      className="w-full rounded-lg border-slate-200 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder={t('profileInfo.namePlaceholder')}
                    />
                  </div>
                  <button
                    type="submit"
                    className={`${theme.button.base} ${theme.button.cta} w-full`}
                  >
                    {t('profileInfo.updateButton')}
                  </button>
                </form>
              </div>
              
              {/* Preferences Form */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-slate-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800">{t('preferences.title')}</h2>
                </div>
                <form onSubmit={handleUpdatePreferences} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('preferences.language')}</label>
                    <select
                      name="defaultLanguage"
                      value={preferencesForm.defaultLanguage}
                      onChange={handlePreferencesChange}
                      className="w-full rounded-lg border-slate-200 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="en">English</option>
                      <option value="hu">Magyar</option>
                      <option value="de">Deutsch</option>
                      <option value="fr">Français</option>
                      <option value="es">Español</option>
                    </select>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="newsletterConsent"
                        name="newsletterConsent"
                        type="checkbox"
                        checked={preferencesForm.newsletterConsent}
                        onChange={handlePreferencesChange}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="newsletterConsent" className="text-sm font-medium text-gray-700">
                        {t('preferences.newsletterConsent')}
                      </label>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    className={`${theme.button.base} ${theme.button.cta} w-full`}
                  >
                    {t('preferences.updateButton')}
                  </button>
                </form>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              {/* Update Password Form */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-slate-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800">{t('security.title')}</h2>
                </div>
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('security.currentPassword')}</label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full rounded-lg border-slate-200 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('security.newPassword')}</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full rounded-lg border-slate-200 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('security.confirmPassword')}</label>
                    <input
                      type="password"
                      name="confirmNewPassword"
                      value={passwordForm.confirmNewPassword}
                      onChange={handlePasswordChange}
                      className="w-full rounded-lg border-slate-200 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className={`${theme.button.base} ${theme.button.cta} w-full`}
                  >
                    {t('security.updateButton')}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Danger Zone - Full Width */}
          <div className="mt-12">
            <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-red-200">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-red-50 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-slate-800">{t('dangerZone.title')}</h2>
              </div>
              
              {!showDeleteConfirm ? (
                <div>
                  <p className="text-slate-600 mb-4">{t('dangerZone.warning')}</p>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-200"
                  >
                    {t('dangerZone.deleteButton')}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">{t('dangerZone.confirmDelete.message')}</p>
                  <div className="max-w-md">
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder={t('dangerZone.confirmDelete.passwordPlaceholder')}
                      className="w-full rounded-lg border-slate-200 shadow-sm focus:border-red-500 focus:ring-red-500"
                    />
                  </div>
                  <div className="flex space-x-4">
                    <button
                      onClick={handleDeleteAccount}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                    >
                      {t('dangerZone.confirmDelete.confirmButton')}
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeletePassword('');
                      }}
                      className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors duration-200"
                    >
                      {t('dangerZone.confirmDelete.cancelButton')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;